import {
  chromeExtensionPubSub,
  useBackgroundMessages,
} from "@src/hooks/chrome/useBackgroundMessages";
import { useGetUrlContexts } from "@src/hooks/useGetUrlContexts";
import usePrevious from "@src/hooks/usePrevious";
import type { ModelContext } from "@src/types/chrome-extension";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { matchPath, type PathMatch } from "react-router";

type UrlContextValue = {
  url: string;
  context: ModelContext | null;
  pathMatch: PathMatch | null;
  urlDidChange: boolean;
};

const UrlContext = React.createContext<UrlContextValue | null>(null);

export const UrlContextProvider = ({ children }: React.PropsWithChildren) => {
  const [url, setUrl] = useState("");

  const previousUrl = usePrevious(url);

  const urlDidChange = Boolean(url && previousUrl && url !== previousUrl);

  const { data: response } = useGetUrlContexts(
    { domain: url },
    {
      query: {
        enabled: Boolean(url),
      },
    },
  );

  const pathname = useMemo(() => {
    if (!url) return null;

    return new URL(url).pathname;
  }, [url]);

  const { context, pathMatch } = useMemo(() => {
    if (!pathname || !response?.data.contexts) {
      return { context: null, pathMatch: null };
    }

    for (const ctx of response.data.contexts) {
      for (const pattern of ctx.patterns) {
        const match = matchPath(pattern.pattern, pathname);
        if (match) {
          return { context: ctx, pathMatch: match };
        }
      }
    }

    return { context: null, pathMatch: null };
  }, [response, pathname]);

  useBackgroundMessages();

  useEffect(() => {
    const unsubscribe = chromeExtensionPubSub.subscribe(
      "urlChanged",
      (data) => {
        setUrl(data.url);
      },
    );

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <UrlContext.Provider value={{ url, context, pathMatch, urlDidChange }}>
      {children}
    </UrlContext.Provider>
  );
};

export function useUrlContext(): UrlContextValue {
  const context = useContext(UrlContext);

  return (
    context ?? { url: "", context: null, pathMatch: null, urlDidChange: false }
  );
}
